import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Car, Home, HeartPulse, PiggyBank, Briefcase, Layers,
  Lock, CheckCircle2, Loader2, ArrowRight, ShieldCheck, Clock3, UserCheck, ChevronDown,
} from "lucide-react";

import { getUtm } from "../utils/utm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "Assurance Automobile", icon: Car, slug: "automobile", route: "/devis/auto", product: "auto" },
  { value: "Assurance Habitation", icon: Home, slug: "habitation", route: "/devis/habitation", product: "habitation" },
  { value: "Complémentaire Santé", icon: HeartPulse, slug: "sante", route: "/devis/sante", product: "sante" },
  { value: "Assurance Vie & Épargne", icon: PiggyBank, slug: "vie", route: "/devis/vie", product: "vie" },
  { value: "Assurance Professionnels / Entreprise", icon: Briefcase, slug: "professionnels", route: "/devis/professionnels", product: "professionnels" },
  { value: "Pack Multirisque Global", icon: Layers, slug: "multirisque", route: "/devis", product: "" },
];

const REASSURANCE = [
  { icon: Clock3, text: "Étude comparative remise sous 24h ouvrées" },
  { icon: UserCheck, text: "Un conseiller dédié, jamais un robot" },
  { icon: ShieldCheck, text: "Sans engagement, sans frais cachés" },
];

const inputCls =
  "w-full rounded-xl border border-ivory/10 bg-white/5 px-5 py-4 text-base text-ivory placeholder:text-ivory/30 transition-all duration-300 gold-focus";

export default function LeadForm() {
  const navigate = useNavigate();
  const [type, setType] = useState("");
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", postal_code: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const selected = TYPES.find((t) => t.value === type);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        insurance_type: type,
        product: selected?.product || "",
        details: slot ? { "Créneau de rappel souhaité": slot } : {},
        ...getUtm(),
      });
      setSubmitted(true);
      if (window.fbq) window.fbq("track", "Lead", { content_name: type });
      toast.success("Demande envoyée avec succès");
    } catch (err) {
      toast.error(
        err.response?.data?.detail
          ? String(err.response.data.detail)
          : "Une erreur est survenue. Veuillez réessayer."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" data-testid="lead-form-section" className="relative py-24 sm:py-36 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 20% 20%, rgba(212,175,55,0.08), transparent 60%)",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-16 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber/90 mb-6 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-amber/60" /> Choisissez votre produit
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-ivory text-balance">
            Demandez votre étude{" "}
            <span className="italic text-amber">gratuite.</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ivory/55 max-w-lg">
            Obtenez un devis comparatif et personnalisé sous 24h ouvrées.
            Sélectionnez le domaine qui vous concerne&nbsp;:
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-3" data-testid="lead-form-type-select" role="radiogroup" aria-label="Type d'assurance souhaité">
            {TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.value}
                  type="button"
                  data-testid={`lead-form-type-option-${t.slug}`}
                  onClick={() => navigate(t.route)}
                  className="group flex items-center gap-3 rounded-2xl border border-ivory/10 bg-white/[0.03] px-5 py-4 text-left text-sm font-medium text-ivory/60 transition-all duration-300 hover:border-amber/60 hover:bg-amber/10 hover:text-amber hover:shadow-[0_0_25px_rgba(212,175,55,0.15)]"
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="flex-1">{t.value}</span>
                  <ArrowRight className="h-4 w-4 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1" />
                </button>
              );
            })}
          </div>

          <ul className="mt-10 space-y-4">
            {REASSURANCE.map((r) => (
              <li key={r.text} className="flex items-center gap-3 text-sm text-ivory/60">
                <r.icon className="h-4 w-4 text-amber" /> {r.text}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="relative"
        >
          <div className="absolute -inset-6 rounded-[2rem] bg-amber/5 blur-2xl" aria-hidden="true" />
          <div className="relative rounded-3xl border hairline bg-cardbg p-8 sm:p-10 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.7)]">
            <AnimatePresence mode="wait">
              {submitted ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-16 text-center"
                  data-testid="lead-form-success"
                >
                  <CheckCircle2 className="mx-auto h-16 w-16 text-amber" />
                  <h3 className="mt-6 font-serif text-3xl font-semibold text-ivory">Merci&nbsp;!</h3>
                  <p className="mt-4 text-base leading-relaxed text-ivory/60 max-w-sm mx-auto">
                    Votre demande a été enregistrée avec succès. Un conseiller
                    ASSU-FRANCE vous recontactera sous 24 heures ouvrées.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setType(""); setSlot(""); setForm({ full_name: "", email: "", phone: "", postal_code: "", city: "", message: "" }); }}
                    data-testid="lead-form-reset-button"
                    className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-amber border-b border-amber/40 pb-1 hover:text-amber-warm transition-colors"
                  >
                    Faire une autre demande
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  exit={{ opacity: 0, scale: 0.97 }}
                  onSubmit={handleSubmit}
                  data-testid="lead-capture-form"
                  className="space-y-5"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ivory/40">
                    Demande express — tous produits
                  </p>
                  <div>
                    <label htmlFor="lead-product" className="mb-2 block text-sm font-medium text-ivory/70">
                      Produit d'assurance *
                    </label>
                    <div className="relative">
                      <select
                        id="lead-product"
                        data-testid="lead-form-product-select"
                        required
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className={`${inputCls} appearance-none pr-12 cursor-pointer`}
                        style={type ? undefined : { color: "rgba(247,245,240,0.35)" }}
                      >
                        <option value="" disabled className="bg-cardbg">
                          Sélectionnez votre produit…
                        </option>
                        {TYPES.map((t) => (
                          <option key={t.value} value={t.value} className="bg-cardbg text-ivory">
                            {t.value}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-5 top-1/2 h-4 w-4 -translate-y-1/2 text-amber/70" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lead-name" className="mb-2 block text-sm font-medium text-ivory/70">
                      Nom & Prénom *
                    </label>
                    <input
                      id="lead-name"
                      data-testid="lead-form-name-input"
                      required
                      value={form.full_name}
                      onChange={set("full_name")}
                      placeholder="ex. Jean Dupont"
                      className={inputCls}
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-email" className="mb-2 block text-sm font-medium text-ivory/70">
                        Adresse email *
                      </label>
                      <input
                        id="lead-email"
                        type="email"
                        data-testid="lead-form-email-input"
                        required
                        value={form.email}
                        onChange={set("email")}
                        placeholder="jean.dupont@exemple.fr"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="lead-phone" className="mb-2 block text-sm font-medium text-ivory/70">
                        Téléphone mobile *
                      </label>
                      <input
                        id="lead-phone"
                        type="tel"
                        data-testid="lead-form-phone-input"
                        required
                        value={form.phone}
                        onChange={set("phone")}
                        placeholder="06 12 34 56 78"
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="lead-postal" className="mb-2 block text-sm font-medium text-ivory/70">
                        Code postal *
                      </label>
                      <input
                        id="lead-postal"
                        data-testid="lead-form-postal-input"
                        required
                        value={form.postal_code}
                        onChange={set("postal_code")}
                        placeholder="ex. 06500"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label htmlFor="lead-city" className="mb-2 block text-sm font-medium text-ivory/70">
                        Ville *
                      </label>
                      <input
                        id="lead-city"
                        data-testid="lead-form-city-input"
                        required
                        value={form.city}
                        onChange={set("city")}
                        placeholder="ex. Menton"
                        className={inputCls}
                      />
                    </div>
                  </div>
                  <div>
                    <span className="mb-2 block text-sm font-medium text-ivory/70">
                      Créneau de rappel souhaité <span className="text-ivory/30">(optionnel)</span>
                    </span>
                    <div className="flex flex-wrap gap-2" data-testid="lead-form-callback-slots">
                      {["Dès que possible", "Le matin (9h – 12h)", "L'après-midi (14h – 18h)", "En soirée (18h – 20h)"].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setSlot(slot === s ? "" : s)}
                          data-testid={`lead-form-slot-${s === "Dès que possible" ? "asap" : s.includes("matin") ? "matin" : s.includes("après") ? "apres-midi" : "soiree"}`}
                          className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
                            slot === s
                              ? "border-amber/60 bg-amber/10 text-amber"
                              : "border-ivory/15 bg-white/[0.03] text-ivory/60 hover:border-ivory/30 hover:text-ivory"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lead-message" className="mb-2 block text-sm font-medium text-ivory/70">
                      Précisions ou besoin particulier <span className="text-ivory/30">(optionnel)</span>
                    </label>
                    <textarea
                      id="lead-message"
                      data-testid="lead-form-message-input"
                      rows={4}
                      value={form.message}
                      onChange={set("message")}
                      placeholder="Décrivez brièvement vos attentes (bonus actuel, surface du logement, effectif entreprise…)"
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-form-submit-button"
                    className="group flex w-full items-center justify-center gap-3 rounded-full bg-amber px-8 py-4 text-base font-semibold text-obsidian transition-all duration-300 hover:bg-amber-warm hover:shadow-[0_0_40px_rgba(212,175,55,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" /> Envoi en cours…
                      </>
                    ) : (
                      <>
                        Recevoir mon devis gratuit
                        <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
                      </>
                    )}
                  </button>
                  <p className="flex items-center justify-center gap-2 text-xs text-ivory/40" data-testid="lead-form-rgpd-note">
                    <Lock className="h-3.5 w-3.5 text-amber/70" />
                    Vos données sont confidentielles et protégées selon la norme RGPD.{" "}
                    <a href="/politique-rgpd/" data-testid="lead-form-privacy-link" className="underline decoration-amber/40 hover:text-amber transition-colors">
                      En savoir plus
                    </a>
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
